import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Popia: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>POPIA Compliance — Sprout Docs</title>
        <meta
          name="description"
          content="How Sprout meets the Protection of Personal Information Act (POPIA): lawful processing, data minimisation, security safeguards, data subject rights, and breach notification."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/security/popia" />
      </Helmet>

      <h1>POPIA Compliance</h1>
      <p className="lead">
        Sprout is built to help South African schools meet their obligations
        under the Protection of Personal Information Act (POPIA). This page
        describes, in plain language, how the platform supports each of the
        eight conditions for lawful processing — and which obligations sit
        with the school versus with us.
      </p>

      <h2>Who is responsible for what (operator vs responsible party)</h2>
      <p>
        Under POPIA, every piece of personal information has a{' '}
        <strong>responsible party</strong> (the entity that decides why and
        how the data is processed) and, optionally, an <strong>operator</strong>{' '}
        (a service provider that processes data on the responsible party's
        behalf under a written mandate, per sections 20–21).
      </p>
      <ul>
        <li>
          <strong>The school is the responsible party.</strong> The school
          decides what to record about each child, what to share with parents,
          how long to retain it, and on what lawful basis. Most importantly,
          the school is responsible for obtaining the upstream consent from
          parents under POPIA s11 and the children's-data competent-person
          consent under s35(1).
        </li>
        <li>
          <strong>Sprout is the operator.</strong> We process the school's data
          only as the school instructs us to, under the contractual mandate
          set out in our <Link to="/legal/terms">Terms of Service</Link>. We
          don't decide what to record, we don't use the data for our own
          purposes, and we hold security safeguards under POPIA s19 — but the
          consent obligation is the school's, not ours.
        </li>
      </ul>

      <h2>Lawful processing and consent</h2>
      <p>
        Schools obtain parental consent at enrollment as part of their existing
        intake process. That consent — collected on paper, by email, or via
        the school's own admissions software — is what makes processing under
        POPIA s11 + s35(1) lawful. By accepting our Terms, the school warrants
        to us that they have obtained that consent for every child they enter
        into Sprout.
      </p>
      <p>
        Sprout provides an optional <code>Consent</code> table that schools
        can use to record each consent inside the platform — timestamp, policy
        version, captured consent text. This is a useful evidence trail for
        compliance review, but it is not where the legal obligation lives. A
        school that records consent on paper and never uses the in-platform
        Consent table is still POPIA-compliant; a school that uses our
        Consent table without obtaining real upstream consent is not.
      </p>

      <h2>Data minimisation</h2>
      <p>
        We only store what's necessary to support educational tracking and
        parent communication: the child's name, date of birth, classroom
        membership, observations, assessments, and any photos a teacher
        attaches to an observation. We do not collect location data,
        biometrics, or behavioural-targeting signals.
      </p>

      <h2>Purpose limitation</h2>
      <p>
        Personal information is used solely for the educational purposes the
        school engaged Sprout for: recording learning, generating reports,
        and sharing those reports with the child's guardians. We do not sell
        data, share it with advertisers, or use it to train external models.
      </p>
      <p>
        Sprout does work with a small set of third-party service providers
        — managed PostgreSQL hosting, transactional email delivery, product
        analytics — without which the platform couldn't function. Each one
        is disclosed by name, region, and scope of data on our{' '}
        <Link to="/legal/subprocessors">Subprocessors</Link> page in
        accordance with POPIA's openness condition (s17). The list is the
        canonical record; this page links rather than duplicates so the
        list and the disclosure can't drift apart.
      </p>

      <h2>Retention</h2>
      <p>
        POPIA section 14 puts the retention decision on the responsible
        party. <strong>The school decides how long to keep each category of
        record</strong> — observations, assessments, photos, consents — based
        on the school's own legal, regulatory, and operational requirements.
        Sprout, as operator, holds the data for as long as the school
        instructs us to and provides the deletion mechanisms the school uses
        to enforce its policy.
      </p>
      <p>
        In practice this means:
      </p>
      <ul>
        <li>
          <strong>By default, Sprout retains everything indefinitely.</strong>{' '}
          We don't run a scheduled purge job. A child's records, observations,
          assessments, and media stay in the platform until the school
          actively deletes them or terminates the contract.
        </li>
        <li>
          <strong>The school can delete at any time.</strong> Per-child hard
          deletion is described in <em>Right to deletion</em> below; it
          cascades through observations, assessments, consents, media files,
          and guardian relationships. Whole-organisation deletion happens at
          contract termination — see section 6 of our{' '}
          <Link to="/legal/terms">Terms of Service</Link>.
        </li>
        <li>
          <strong>Backups are the practical floor.</strong> Database and
          media backups are immutable archives that protect against
          ransomware and operator error. After the school deletes a record,
          residual copies survive in backup archives until those archives
          expire (typically 30 days for daily rolling backups, longer for
          periodic archives). After that the data is gone everywhere.
        </li>
        <li>
          <strong>Audit-log rows outlive the data they audit.</strong> POPIA
          s17 requires the school to be able to demonstrate who accessed a
          child's record and when, including <em>after</em> the record is
          deleted. Audit rows are kept by UUID only (no name in the stored
          row), so the trail survives without the PII it references.
        </li>
      </ul>
      <p>
        If a school needs an enforced retention policy — for example, a
        rule that observations are auto-purged seven years after a child
        leaves — that's a feature we'd build under a custom mandate. Get
        in touch if it applies to you.
      </p>

      <h2>Security safeguards</h2>
      <ul>
        <li>TLS 1.2+ for all data in transit.</li>
        <li>AES-256 encryption at rest, managed by the underlying PostgreSQL provider.</li>
        <li>Encrypted, off-site database backups.</li>
        <li>Role-based access control: teachers see only their own classrooms; guardians see only their own children.</li>
        <li>Audit logs of access to child records, retained for compliance review.</li>
      </ul>

      <h2>Data subject rights</h2>
      <p>
        POPIA grants individuals (or, for minors, their guardians) the right
        to access, correct, and delete personal information. Sprout supports
        these directly:
      </p>
      <ul>
        <li>
          <strong>Right of access</strong> — see{' '}
          <Link to="/docs/security/data-export">Data Export</Link>. Available on every plan, including Seedling.
        </li>
        <li>
          <strong>Right to correction</strong> — guardians can request
          corrections through the school; staff can edit observations and
          assessments at any time.
        </li>
        <li>
          <strong>Right to deletion</strong> — when a child leaves the
          school, an organisation admin can permanently delete the child
          record from <em>Children → Delete</em>. The deletion is a hard
          cascade: it removes the child profile, every observation and
          assessment about that child, the photo and video files in S3,
          all guardian relationships and pending invitations, and the
          school's recorded consent records. The action is irreversible
          from the application — see <em>What deletion does not do</em>{' '}
          below for the backup caveat. Audit-log entries about the child
          are retained because POPIA s17 requires audit trails to survive
          the records they audit, but the audit row references the child
          by UUID only, not by name.
        </li>
      </ul>

      <h3>What deletion does not do</h3>
      <p>
        Two things deletion intentionally does <em>not</em> reach:
      </p>
      <ul>
        <li>
          <strong>Backups within the retention window.</strong> Database
          and media backups are immutable archives that protect against
          ransomware and operator error. Deletes don't propagate into
          existing backup archives — instead, each backup expires after
          the standard retention window (typically 30 days for daily
          rolling backups, longer for periodic archives), and after that
          the deleted record is gone everywhere. If a school needs faster
          backup-side erasure for a specific case, contact us.
        </li>
        <li>
          <strong>Audit-log rows referencing the child.</strong> POPIA
          s17 requires the school to be able to demonstrate who accessed
          a child's record and when — including <em>after</em> the record
          is deleted. We keep audit rows by UUID only (no name in the
          row's stored fields), so the trail survives without the PII.
        </li>
      </ul>

      <h2>Breach notification</h2>
      <p>
        We follow the Information Regulator's guidance on data breaches.
        If a security incident affecting personal information is confirmed,
        we notify affected schools without undue delay so they can in turn
        notify guardians within the seventy-two-hour window where required.
      </p>

      <h2>Information Officer</h2>
      <p>
        Privacy and POPIA queries can be sent to{' '}
        <a href="mailto:privacy@sprout.education">privacy@sprout.education</a>.
        For broader questions, our{' '}
        <Link to="/contact">contact page</Link> lists other channels.
      </p>

      <p>
        Related: <Link to="/docs/security/data-residency">Data Residency</Link>{' '}
        explains where customer data physically lives.
      </p>
    </>
  );
};

export default Popia;
